import { isInvalid } from '../type/isInvalid';

const DEFAULT_COLORS = {
  up: '#FF2436',
  down: '#07AB4B',
  even: '',
};

const DEFAULT_CLASSNAMES = {
  up: 'stock-color-up',
  down: 'stock-color-down',
  even: 'stock-color-even',
};

// 合并配置
function mergeConfig<T extends object>(defaults: T, custom?: Partial<T>): T {
  return { ...defaults, ...custom };
}

// 转换为数字，isInvalid 视为 NaN
function toNumeric(value: number | string): number {
  if (isInvalid(value)) return NaN;
  const num = Number(value);
  return isNaN(num) ? NaN : num;
}

// 清理字符串中的单位（末尾的百分号和中文单位），保留数字、小数点、正负号、科学计数法和十六进制
function cleanValue(value: unknown): string | number {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  // 先尝试直接转换，如果成功就不清理
  if (!isNaN(Number(trimmed))) return trimmed;
  // 否则去掉末尾的百分号或中文单位
  const cleaned = trimmed.replace(/(%|万|亿)$/, '');
  return cleaned || trimmed;
}

/**
 * 根据基准值比较当前值，返回对应的颜色样式
 *
 * 该函数会比较当前值与基准值的大小关系，并根据比较结果返回相应的颜色样式。
 * 同时返回颜色值和CSS类名，支持自定义配置。
 * 当当前值无效时，返回even状态。
 *
 * @param currentValue - 当前要比较的值，支持数字或字符串
 * @param options - 配置选项
 * @param options.baseValue - 基准值，默认为0
 * @param options.colors - 自定义颜色配置
 * @param options.classNames - 自定义CSS类名配置
 * @returns 根据比较结果返回对应的颜色样式
 * @throws {TypeError} 当 baseValue 不是数字时抛出
 *
 * @example
 * ```ts
 * // 基本用法 - 与0比较
 * getComparisonStyle(10);
 * // => { type: 'up', color: '#FF2436', className: 'stock-color-up' }
 *
 * // 字符串数值
 * getComparisonStyle('10');
 * // => { type: 'up', color: '#FF2436', className: 'stock-color-up' }
 *
 * // 与指定基准值比较
 * getComparisonStyle(10, { baseValue: 5 });
 * // => { type: 'up', color: '#FF2436', className: 'stock-color-up' }
 *
 * // 当前值无效时返回even
 * getComparisonStyle('invalid', { baseValue: 5 });
 * // => { type: 'even', color: '', className: 'stock-color-even' }
 *
 * // 自定义颜色配置
 * getComparisonStyle(10, {
 *   baseValue: 5,
 *   colors: {
 *     up: '#00ff00',
 *     down: '#ff0000',
 *     even: '#999999'
 *   }
 * });
 * // => { type: 'up', color: '#00ff00', className: 'stock-color-up' }
 *
 * // 自定义CSS类名配置
 * getComparisonStyle('8', {
 *   baseValue: 10,
 *   classNames: {
 *     up: 'custom-up',
 *     down: 'custom-down',
 *     even: 'custom-even'
 *   }
 * });
 * // => { type: 'down', color: '#07AB4B', className: 'custom-down' }
 * ```
 *
 * @since 1.0.0
 */
export function getComparisonStyle(
  currentValue: number | string,
  options?: {
    baseValue?: number;
    colors?: {
      up?: string;
      down?: string;
      even?: string;
    };
    classNames?: {
      up?: string;
      down?: string;
      even?: string;
    };
  },
): {
  type: 'up' | 'down' | 'even';
  color: string;
  className: string;
} {
  const baseValue = options?.baseValue ?? 0;
  if (typeof baseValue !== 'number' || isNaN(baseValue)) {
    throw new TypeError('baseValue 必须是有效的数字');
  }

  const cleanedValue = cleanValue(currentValue);
  const numericValue = toNumeric(cleanedValue);
  const colors = mergeConfig(DEFAULT_COLORS, options?.colors);
  const classNames = mergeConfig(DEFAULT_CLASSNAMES, options?.classNames);

  let type: 'up' | 'down' | 'even';
  if (isNaN(numericValue)) {
    type = 'even';
  } else if (numericValue > baseValue) {
    type = 'up';
  } else if (numericValue < baseValue) {
    type = 'down';
  } else {
    type = 'even';
  }

  return {
    type,
    color: colors[type],
    className: classNames[type],
  };
}